<template>
  <ElForm
    :model="importDetails"
    ref="import-guests-form"
    label-position="top"
    label-width="120px"
    status-icon
    @submit.native.prevent
  >
    <ElFormItem label="Match the columns">
      <div class="overflow-x-auto">
        <div class="min-w-full inline-flex items-center px-10 py-15 bg-gray-200 border-b border-gray-300 rounded-t">
          <p class="w-45 text-center">
            #
          </p>
          <ElSelect
            v-for="(col, colIndex) in importFileData.fileColumns"
            :key="`file-column-${colIndex}`"
            v-model="columnValues[colIndex]"
            placeholder="Skip"
            class="column-select w-185"
            :class="{
              'mr-15': colIndex !== importFileData.fileColumns.length - 1,
              'skip-column': !columnValues[colIndex],
            }"
            @change="onColumnValueChange(colIndex, columnValues[colIndex])"
          >
            <ElOption
              v-for="item in columnOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            >
              <span
                :class="{
                  'font-bold': item.value === 'firstName' || item.value === 'lastName',
                }"
              >{{ item.label }}</span>
            </ElOption>
          </ElSelect>
        </div>
        <div>
          <div
            v-for="(row, key) in importPreviewData.previewRows"
            :key="key"
            class="guest-row min-w-full inline-flex px-10 py-2"
            :class="{
              'font-light text-gray-400 italic': key < importFileData.invalidRowsIndexLimit,
            }"
          >
            <p class="w-45 font-bold text-center">
              {{ key + 1 }}
            </p>
            <p
              v-for="(col, firstRowsIndex) in importFileData.fileColumns"
              :key="col.key"
              :title="row[col.key]"
              class="w-185 truncate"
              :class="{
                'mr-15': firstRowsIndex !== importFileData.fileColumns.length - 1,
              }"
            >
              {{ row[col.key] }}
            </p>
          </div>
          <p
            v-if="importPreviewData.hiddenRowsCount"
            class="font-bold px-10 py-2"
          >
            {{ hiddenRowCountLabel }}
          </p>
          <div
            v-if="importPreviewData.previewLastRow"
            key="preview-last-row"
            class="min-w-full inline-flex px-10 py-2"
          >
            <p class="w-45 font-bold text-center">
              {{ importPreviewData.previewLastRowIndex }}
            </p>
            <p
              v-for="(col, lastColIndex) in importFileData.fileColumns"
              :key="col.key"
              :title="importPreviewData.previewLastRow[col.key]"
              class="w-185 truncate"
              :class="{
                'mr-15': lastColIndex !== importFileData.fileColumns.length - 1,
              }"
            >
              {{ importPreviewData.previewLastRow[col.key] }}
            </p>
          </div>
        </div>
      </div>
      <div
        v-if="guestsToImportLabel"
        class="px-20 py-5 bg-gray-200 border-t border-gray-300 rounded-b"
      >
        <FontAwesomeIcon
          :icon="['fas', 'info-circle']"
          class="align-middle text-2xl mr-10"
        />
        <span class="align-middle">
          {{ guestsToImportLabel }}
        </span>
      </div>
    </ElFormItem>

    <div class="flex flex-col tablet:flex-row">
      <ElFormItem
        label="Set guest's status to"
        prop="guestStatus"
        class="flex-no-shrink"
      >
        <ElSelect
          v-model="importDetails.guestStatus"
          placeholder="Select a status"
        >
          <ElOption
            v-for="option in statusOptions"
            :key="option.value"
            :label="option.label"
            :value="option.value"
          >
          </ElOption>
        </ElSelect>
      </ElFormItem>
      <TransitionCollapse>
        <p
          v-if="[0, 4, 5].includes(importDetails.guestStatus)"
          class="flex-grow"
        >
          <span class="flex flex-row items-center p-15 mb-20 bg-gray-100 rounded-20 tablet:mb-0 tablet:ml-20">
            <FontAwesomeIcon
              :icon="['fas', 'info-circle']"
              class="text-3xl text-warning mr-15"
            />
            <span class="font-medium">
              Guests with this status will not be visible in the Android and iOS Apps
            </span>
          </span>
        </p>
      </TransitionCollapse>
    </div>

    <ElFormItem
      v-if="(!guestsIsEmpty && !isImportingGuests) || (guestsIsEmpty && isImportingGuests)"
      label="Delete existing guests"
      prop="deleteExistingGuests"
    >
      <ElSwitch v-model="importDetails.deleteExistingGuests" />
      <TransitionCollapse>
        <p v-if="importDetails.deleteExistingGuests">
          <span class="flex flex-row items-center">
            <FontAwesomeIcon
              :icon="['fas', 'exclamation-triangle']"
              class="text-2xl text-warning mr-10"
            />
            {{ deleteGuestCount }}
          </span>
        </p>
      </TransitionCollapse>
    </ElFormItem>
  </ElForm>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { guestProperties } from '@/utils/utilityFunctions';
import guestPluralRules from '@/utils/pluralRules/guest';
import rowPluralRules from '@/utils/pluralRules/row';
import TransitionCollapse from '@/components/transition/TransitionCollapse';

const previewRowCount = 11;

export default {
  props: {
    showBarcodeValue: {
      type: Boolean,
      default: false,
    },
    statusOptions: {
      type: Array,
      default: () => [],
    },
  },
  components: {
    TransitionCollapse,
  },
  data() {
    const { importDetails } = this.$store.state.guest.import;
    const columnOptions = [];

    // Generating column options
    columnOptions.push({
      value: '',
      label: 'Skip column',
    });
    Object.entries(guestProperties).forEach(([guestProperty, value]) => { // TODO: Handle currentEventFirstNameFirstOrder
      if (value.isImportOption) {
        if (guestProperty === 'barcodeValue') {
          if (this.showBarcodeValue) {
            columnOptions.push({
              value: guestProperty,
              label: value.label,
            });
          }
        } else {
          columnOptions.push({
            value: guestProperty,
            label: value.label,
          });
        }
      }
    });

    const columnValues = this.$store.state.guest.import.selectedColumns.slice(0);

    return {
      importDetails,
      activeUncollapsedItem: [],
      columnOptions,
      columnValues,
    };
  },
  watch: {
    importDetails: {
      immediate: true,
      deep: true,
      handler(value) {
        this.updateImportDetails({ ...value });
      },
    },
    'importDetails.guestStatus': {
      handler() {
        this.generateGuestData();
      },
    },
  },
  computed: {
    ...mapState('guest', {
      guestsCounts: 'guestsCounts',
    }),
    ...mapState('guest/import', {
      isImportingGuests: 'isImportingGuests',
      selectedFiles: 'selectedFiles',
      selectedColumns: 'selectedColumns',
      importFileData: 'importFileData',
      guestsToImport: 'guestsToImport',
    }),
    ...mapGetters('guest', {
      guestsIsEmpty: 'guestsIsEmpty',
    }),
    ...mapGetters('guest/import', {
      columnSelectionIsValid: 'columnSelectionIsValid',
    }),
    importPreviewData() {
      let previewRows = [];
      let hiddenRowsCount = 0;
      let previewLastRowIndex = 0;
      let previewLastRow = null;

      if (this.importFileData.fileData.length <= previewRowCount) {
        previewRows = this.importFileData.fileData;
      } else {
        previewRows = this.importFileData.fileData.slice(0, previewRowCount - 1);
        hiddenRowsCount = this.importFileData.fileData.length - previewRowCount;
        previewLastRow = this.importFileData.fileData[this.importFileData.fileData.length - 1];
        previewLastRowIndex = this.importFileData.fileData.length;
      }

      return {
        previewRows,
        hiddenRowsCount,
        previewLastRow,
        previewLastRowIndex,
      };
    },
    deleteGuestCount() {
      return `This will delete your ${guestPluralRules(this.guestsCounts.mainGuest + this.guestsCounts.extraGuest)}.`;
    },
    hiddenRowCountLabel() {
      return `...${rowPluralRules(this.importPreviewData.hiddenRowsCount)} hidden...`;
    },
    guestsToImportLabel() {
      if (this.guestsToImport.length === 0) return 'Match at least the first or the last name and make sure that your file contains at least one guest.';
      if (this.guestsToImport.length === 1) return '1 guest was detected.';
      return `${this.guestsToImport.length} guests were detected.`;
    },
  },
  methods: {
    ...mapActions('guest/import', {
      updateImportDetails: 'updateImportDetails',
      updateSelectedColumns: 'updateSelectedColumns',
      generateGuestData: 'generateGuestData',
    }),
    onColumnValueChange(colIndex, newValue) {
      const selectedColumns = this.selectedColumns.slice(0);
      const colIndexOfExistingColValue = selectedColumns.findIndex((colValue) => colValue === newValue);

      if (colIndexOfExistingColValue !== -1 && newValue !== '') {
        // console.log('# Col was previously selected');
        // console.log(colIndexOfExistingColValue, newValue);
        this.$set(this.columnValues, colIndexOfExistingColValue, selectedColumns[colIndex]);
        // this.columnValues[colIndex] = this.selectedColumns[colIndex];
        selectedColumns[colIndexOfExistingColValue] = selectedColumns[colIndex];
      }
      selectedColumns[colIndex] = newValue;
      // console.log('# SelectedColumns: ', selectedColumns);

      // console.log(selectedColumns);
      this.updateSelectedColumns(selectedColumns);
      this.generateGuestData();
    },
  },
};
</script>

<style scoped>
.guest-row {
  &:nth-child(even) {
    @apply bg-gray-100;
  }
}

.column-select {
  >>> {
    .el-input__inner {
      @apply bg-transparent rounded-5 border-primary-light;
    }

    .el-select__caret {
      @apply font-bold text-primary-light;
    }
  }

  &.skip-column {
    >>> {
      .el-input__inner {
        @apply text-primary-light border-gray-400;
      }

      .el-select__caret {
        @apply font-normal text-primary-light;
      }
    }
  }
}
</style>
